/**
 * Minimal one-shot FX pool: expanding, fading emissive rings. Used for
 * portal traversals and incinerations until the real juice pass lands.
 */
import * as THREE from "three";

interface Fx {
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
  life: number;
  maxLife: number;
  startScale: number;
  endScale: number;
  peak: number;
}

export class FxPool {
  readonly group = new THREE.Group();
  private readonly active: Fx[] = [];
  private readonly geo = new THREE.RingGeometry(0.7, 1, 32);

  /** `peak` caps the ring's starting opacity — additive rings stack, so bursts
   *  that can coincide (portal traversal volleys) pass a soft peak to stay
   *  below white-out (LOCKED-DESIGN 18.10 4B capture / 12.3 flash policy). */
  spawn(pos: THREE.Vector3, normal: THREE.Vector3, color: number, size = 1, life = 0.35, peak = 0.9): void {
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: peak,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(this.geo, mat);
    mesh.position.copy(pos);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
    this.group.add(mesh);
    this.active.push({ mesh, mat, life, maxLife: life, startScale: 0.4 * size, endScale: 1.9 * size, peak });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const fx = this.active[i]!;
      fx.life -= dt;
      if (fx.life <= 0) {
        this.group.remove(fx.mesh);
        fx.mat.dispose();
        this.active.splice(i, 1);
        continue;
      }
      const t = 1 - fx.life / fx.maxLife;
      const s = fx.startScale + (fx.endScale - fx.startScale) * t;
      fx.mesh.scale.set(s, s, s);
      fx.mat.opacity = fx.peak * (1 - t);
    }
  }
}
